import { useGetDefaultTenantTimeRange } from '@modules/common/hooks';
import { decompressURLSearchParams } from '@modules/common/utils';
import { populatePayloadFromSearchParams } from '@modules/common/utils/populatePayloadFromSearchParams';
import { parseStringifiedDuration } from '@modules/feed/utils/parseStringifiedDuration';
import { useListSourceDetailsQuery } from 'generated/graphql';
import { useCallback, useMemo } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { QuantifyActionType } from '../store/quantifyActionType';
import { useQuantifyStore } from '../store/quantifyStore';
import { QuantifyQuery } from '../store/types';
import { useGetSelectedGroupByOption } from './useGetSelectedGroupByOption';
import { useGetSelectedMeasuredAsCriteriaOption } from './useGetSelectedMeasuredAsCriteriaOption';

export const useSetQuantifyStoreFromSearchParams = () => {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);

  const searchParams = useMemo(
    () =>
      new URLSearchParams(decompressURLSearchParams(document.location.search)),
    [],
  );
  const { isLoading: isLoadingGroupByOption, selectedGroupByOption } =
    useGetSelectedGroupByOption(searchParams.get('groupByValue') || undefined);

  const defaultTimeRange = useGetDefaultTenantTimeRange();

  const queryPayload = useMemo(() => {
    const queryPayload: Partial<QuantifyQuery> = {};

    populatePayloadFromSearchParams(
      queryPayload,
      [
        'duration',
        'aggregatePeriod',
        'whereClause',
        'compareQuery',
        'formulaQuery',
        'shouldShowAnomalies',
        'anomalyThreshold',
        'sortBy',
        'compareMetadata',
        'visualisationType',
        'isCoOccurring',
      ],
      decompressURLSearchParams,
    );

    queryPayload.duration = queryPayload.duration
      ? parseStringifiedDuration(queryPayload.duration)
      : defaultTimeRange;

    return queryPayload;
  }, [defaultTimeRange]);

  if (!queryPayload.formulaQuery?.eqlFunction) {
    queryPayload.formulaQuery = {
      eqlFunction: EQLFunctionName.Count,
      ...queryPayload.formulaQuery,
    };
  }

  const {
    isLoading: isLoadingMeasuredAsCriteriaOption,
    selectedMeasuredAsCriteriaOption,
  } = useGetSelectedMeasuredAsCriteriaOption({
    formulaID: queryPayload.formulaQuery?.eqlFunction ?? EQLFunctionName.Count,
    measuredAsCriteria:
      searchParams.get('measuredAsCriteriaValue') || undefined,
    groupBy: selectedGroupByOption,
  });

  // we fire EQL queries to fetch data which is dependent on source details
  // we need to wait till source details are available before we fetch report
  // data
  const { loading: isLoadingSourceDetails } = useListSourceDetailsQuery({});

  const isLoadingRequiredData =
    isLoadingGroupByOption ||
    isLoadingSourceDetails ||
    isLoadingMeasuredAsCriteriaOption;

  const setQuantifyStoreFromSearchParams = useCallback(() => {
    if (!isLoadingRequiredData) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          ...queryPayload,
          groupByField: selectedGroupByOption,
          formulaQuery: {
            ...queryPayload.formulaQuery,
            criteria: selectedMeasuredAsCriteriaOption,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isLoadingRequiredData,
    queryPayload,
    selectedGroupByOption?.field,
    selectedMeasuredAsCriteriaOption?.field,
  ]);

  return [
    setQuantifyStoreFromSearchParams,
    { isLoading: isLoadingRequiredData },
  ] as const;
};
